import { useTranslation } from 'react-i18next'
import { Stamp } from '@/components/ui/Stamp'
import type { PlayerStats } from '@/lib/stoccopong-stats'
import { cn } from '@/lib/utils'
import { CupHeatmap } from './CupHeatmap'

type PlayerStatsCardProps = { player: { id: string; name: string }; stats: PlayerStats; cupCount: 3 | 6 }

/** Scheda di un giocatore nel formato selezionato: badge apertura/chiusura, heatmap dei bicchieri, media
 * a partita, bicchiere preferito/evitato; l'airball resta muto (`text-ink-soft`) quando è zero. */
export function PlayerStatsCard({ player, stats, cupCount }: PlayerStatsCardProps) {
  const { t } = useTranslation()

  return (
    <div data-testid={`player-stats-${player.id}`} className="rounded-xl border border-line bg-paper-deep/60 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate font-display text-xl text-ink">{player.name}</span>
          {stats.openerBadge && <Stamp tone="ink">{t('stoccoPong.stats.openerBadge')}</Stamp>}
          {stats.closerBadge && <Stamp tone="coral">{t('stoccoPong.stats.closerBadge')}</Stamp>}
        </div>
        <span className="shrink-0 font-display text-2xl tabular-nums text-ink">{stats.cups}</span>
      </div>

      <div className="my-4 flex justify-center">
        <CupHeatmap heatmap={stats.heatmap} cupCount={cupCount} />
      </div>

      <dl className="grid grid-cols-2 gap-3 border-t border-line pt-3 text-center sm:grid-cols-4">
        <div>
          <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.avgFor')}</dt>
          <dd className="font-display text-base tabular-nums text-ink">{stats.average.toFixed(1)}</dd>
        </div>
        <div>
          <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.openers')}</dt>
          <dd className="font-display text-base tabular-nums text-ink">{stats.openers}</dd>
        </div>
        <div>
          <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.closers')}</dt>
          <dd className="font-display text-base tabular-nums text-ink">{stats.closers}</dd>
        </div>
        <div>
          <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.airballs')}</dt>
          <dd className={cn('font-display text-base tabular-nums', stats.airballs > 0 ? 'text-coral-ink' : 'text-ink-soft')}>
            {stats.airballs > 0 ? stats.airballs : '–'}
          </dd>
        </div>
        {stats.favorite !== null && (
          <div>
            <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.favorite')}</dt>
            <dd className="font-display text-base text-sea-ink">
              #{stats.favorite + 1} <span className="font-body text-xs text-ink-soft">{t('stoccoPong.stats.hits', { count: stats.heatmap[stats.favorite] })}</span>
            </dd>
          </div>
        )}
        {stats.avoided !== null && (
          <div>
            <dt className="font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft">{t('stoccoPong.stats.avoided')}</dt>
            <dd className="font-display text-base text-ink-soft">
              #{stats.avoided + 1} <span className="font-body text-xs text-ink-soft">{t('stoccoPong.stats.hits', { count: stats.heatmap[stats.avoided] })}</span>
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}
